import { motion } from "framer-motion";
import { MessageCircle, Brain, CalendarDays, Repeat } from "lucide-react";

const steps = [
  {
    num: "01",
    icon: MessageCircle,
    title: "Lead Texts In",
    description: "Someone finds your business and sends a text. Could be 2 PM or 2 AM — doesn't matter.",
  },
  {
    num: "02",
    icon: Brain,
    title: "AI Engages Instantly",
    description: "Our AI responds in seconds with natural, human-sounding texts. Asks qualifying questions, handles objections.",
  },
  {
    num: "03",
    icon: CalendarDays,
    title: "Appointment Booked",
    description: "Checks your live calendar, suggests time slots, confirms the booking. Sends reminders before the appointment.",
  },
  {
    num: "04",
    icon: Repeat,
    title: "Revenue Grows",
    description: "After service, follows up for reviews, upsells add-ons, and prompts rebooking. One-time clients become regulars.",
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 relative scroll-mt-24">
      {/* Subtle gradient line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-24 bg-gradient-to-b from-transparent to-zinc-800" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-blue-400 text-sm font-semibold tracking-wide uppercase mb-4 block">How It Works</span>
          <h2
            data-testid="how-it-works-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            From lead to loyal customer in 4 steps
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto">
            No manual work. No missed leads. Just revenue on autopilot.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connecting line for desktop */}
          <div className="hidden lg:block absolute top-[60px] left-[12%] right-[12%] h-px bg-zinc-800" />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.12 }}
                data-testid={`step-${i}`}
                className="relative text-center"
              >
                <div className="relative z-10 w-[72px] h-[72px] rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto mb-6">
                  <Icon size={28} className="text-blue-400" />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {step.num}
                  </span>
                </div>

                <h3 className="text-white text-lg font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
